// src/components/common/ErrorRecovery.jsx
import React, { useState } from 'react';
import {
    Box, Container, Typography, Button, Card, CardContent,
    Alert, Collapse, IconButton, Chip, Stack, Divider,
} from '@mui/material';
import {
    Error as ErrorIcon,
    Refresh as RefreshIcon,
    Home as HomeIcon,
    BugReport as BugReportIcon,
    ExpandMore as ExpandIcon,
    ContentCopy as CopyIcon,
    CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Enhanced error boundary with recovery options
export const ErrorRecovery = ({ 
    error, 
    resetError, 
    title = "Something went wrong",
    showTechnicalDetails = false,
    customActions = null 
}) => {
    const navigate = useNavigate();
    const [showDetails, setShowDetails] = useState(false);
    const [copied, setCopied] = useState(false);

    // Get user-friendly error message
    const getErrorMessage = (error) => {
        if (error?.message?.includes('Network')) {
            return "We're having trouble connecting to our servers. Please check your internet connection and try again.";
        }
        if (error?.message?.includes('404')) {
            return "The page or resource you're looking for doesn't exist. It may have been moved or deleted.";
        }
        if (error?.message?.includes('500')) {
            return "Our servers are experiencing issues. Our team has been notified and is working on a fix.";
        }
        if (error?.message?.includes('timeout')) {
            return "The request is taking longer than expected. Please try again or contact support if the issue persists.";
        }
        return "An unexpected error occurred. Please try refreshing the page or contact support if the problem continues.";
    };

    // Get error severity level
    const getErrorSeverity = (error) => {
        if (error?.message?.includes('Network') || error?.message?.includes('timeout')) {
            return 'warning';
        }
        if (error?.message?.includes('404')) {
            return 'info';
        }
        return 'error';
    };

    // Copy error details to clipboard
    const copyErrorDetails = async () => {
        const errorDetails = `
Error: ${error?.message || 'Unknown error'}
Stack: ${error?.stack || 'No stack trace available'}
URL: ${window.location.href}
User Agent: ${navigator.userAgent}
Timestamp: ${new Date().toISOString()}
        `.trim();

        try {
            await navigator.clipboard.writeText(errorDetails);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy error details:', err);
        }
    };

    // Report error to support (placeholder for actual implementation)
    const reportError = () => {
        const errorReport = {
            message: error?.message,
            stack: error?.stack,
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
        };

        // In a real app, you'd send this to your error reporting service
        console.log('Error reported:', errorReport);
        
        // For now, open email client
        const subject = encodeURIComponent('AutoPostureCV Error Report');
        const body = encodeURIComponent(`
Error Details:
${JSON.stringify(errorReport, null, 2)}

Please describe what you were doing when this error occurred:
[Your description here]
        `);
        
        window.open(`mailto:mikeinyang975@gmail.com?subject=${subject}&body=${body}`);
    };

    const severity = getErrorSeverity(error);
    const errorMessage = getErrorMessage(error);

    return (
        <Container maxWidth="md" sx={{ py: 8 }}>
            <Card sx={{ textAlign: 'center', p: 4 }}>
                <CardContent>
                    {/* Error Icon */}
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            bgcolor: severity === 'error' ? 'error.light' : 
                                     severity === 'warning' ? 'warning.light' : 'info.light',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 3,
                            opacity: 0.9,
                        }}
                    >
                        <ErrorIcon 
                            sx={{ 
                                fontSize: 40, 
                                color: severity === 'error' ? 'error.main' : 
                                       severity === 'warning' ? 'warning.main' : 'info.main'
                            }} 
                        />
                    </Box>

                    {/* Error Type Badge */}
                    <Chip
                        label={severity === 'error' ? 'Application Error' : 
                              severity === 'warning' ? 'Connection Issue' : 'Not Found'}
                        color={severity}
                        sx={{ mb: 2 }}
                    />

                    {/* Title */}
                    <Typography variant="h4" gutterBottom fontWeight={700}>
                        {title}
                    </Typography>

                    {/* User-friendly message */}
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
                        {errorMessage}
                    </Typography>

                    {/* Primary Actions */}
                    <Stack 
                        direction={{ xs: 'column', sm: 'row' }} 
                        spacing={2} 
                        justifyContent="center"
                        sx={{ mb: 3 }}
                    >
                        <Button 
                            variant="contained" 
                            onClick={resetError}
                            startIcon={<RefreshIcon />}
                            size="large"
                        >
                            Try Again
                        </Button>
                        
                        <Button 
                            variant="outlined" 
                            onClick={() => navigate('/')}
                            startIcon={<HomeIcon />}
                            size="large"
                        >
                            Go Home
                        </Button>
                        
                        <Button 
                            variant="text" 
                            onClick={reportError}
                            startIcon={<BugReportIcon />}
                            size="large"
                        >
                            Report Issue
                        </Button>
                    </Stack>

                    {/* Custom Actions */}
                    {customActions && (
                        <>
                            <Divider sx={{ my: 3 }} />
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                {customActions}
                            </Box>
                        </>
                    )}

                    {/* Technical Details (expandable) */}
                    {showTechnicalDetails && (
                        <Box sx={{ mt: 3 }}>
                            <Button
                                onClick={() => setShowDetails(!showDetails)}
                                endIcon={
                                    <ExpandIcon 
                                        sx={{ 
                                            transform: showDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.2s'
                                        }} 
                                    />
                                }
                                size="small"
                                color="inherit"
                            >
                                Technical Details
                            </Button>
                            
                            <Collapse in={showDetails}>
                                <Alert 
                                    severity="info" 
                                    sx={{ mt: 2, textAlign: 'left' }}
                                    action={
                                        <IconButton
                                            size="small"
                                            onClick={copyErrorDetails}
                                            color="inherit"
                                        >
                                            {copied ? <CheckIcon /> : <CopyIcon />}
                                        </IconButton>
                                    }
                                >
                                    <Typography variant="body2" fontFamily="monospace" sx={{ mb: 1 }}>
                                        <strong>Error:</strong> {error?.message || 'Unknown error'}
                                    </Typography>
                                    
                                    {error?.stack && (
                                        <Typography 
                                            variant="body2" 
                                            fontFamily="monospace" 
                                            sx={{ 
                                                whiteSpace: 'pre-wrap', 
                                                fontSize: '0.75rem',
                                                maxHeight: 200,
                                                overflow: 'auto',
                                                bgcolor: 'rgba(0,0,0,0.05)',
                                                p: 1,
                                                borderRadius: 1,
                                            }}
                                        >
                                            {error.stack}
                                        </Typography>
                                    )}
                                </Alert>
                            </Collapse>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Container>
    );
};

// Inline error component for smaller errors
export const InlineError = ({ 
    error, 
    onRetry, 
    title = "Something went wrong",
    compact = false 
}) => {
    return (
        <Alert 
            severity="error" 
            sx={{ borderRadius: 2 }}
            action={
                onRetry && (
                    <Button color="inherit" size="small" onClick={onRetry}>
                        Retry
                    </Button>
                )
            }
        >
            {!compact && (
                <Typography variant="subtitle2" gutterBottom>
                    {title}
                </Typography>
            )}
            <Typography variant="body2">
                {error?.message || "An unexpected error occurred"}
            </Typography>
        </Alert>
    );
};

// Network error component
export const NetworkError = ({ onRetry }) => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" gutterBottom color="text.secondary">
            Connection Problem
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please check your internet connection and try again.
        </Typography>
        {onRetry && (
            <Button variant="outlined" onClick={onRetry} startIcon={<RefreshIcon />}>
                Try Again
            </Button>
        )}
    </Box>
);

export default ErrorRecovery;